import { useState, useMemo, useCallback, memo, useRef } from 'react'
import { CategoriesBaseProps, CategoryItem, defaultCategories, categoryColors } from './CategoriesBase'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { InfoTooltip } from '@/components/ui/info-tooltip'

const ProjectLogo = memo(({ logo, alt, color }: { logo?: string | null; alt: string; color?: string }) => {
  const [imgError, setImgError] = useState(false)

  return (
    <div
      className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: color || '#949494' }}
    >
      {!imgError && logo ? (
        <img
          src={logo}
          alt={alt}
          className="w-7 h-7 rounded-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <span className="text-[9px] font-medium text-white dark:text-[#fdfdfd]">
          {alt.charAt(0).toUpperCase()}
        </span>
      )}
    </div>
  )
})

ProjectLogo.displayName = 'ProjectLogo'

const CategoryItemComponent = memo(({
  item,
  index,
  onItemClick
}: {
  item: CategoryItem
  index: number
  onItemClick?: (item: CategoryItem) => void
}) => {
  const color = categoryColors[index % categoryColors.length]

  const handleClick = useCallback(() => {
    onItemClick?.(item)
  }, [item, onItemClick])

  return (
    <div
      className="bg-white dark:bg-[#1c1c1c] h-[52px] flex items-center justify-between pl-3 pr-[18px] py-3 cursor-pointer hover:opacity-90 transition-opacity"
      onClick={handleClick}
    >
      <div className="flex gap-[10px] items-center min-w-0">
        <ProjectLogo
          logo={item.logo}
          alt={item.project_name || 'Project'}
          color={color}
        />
        <p className="font-plus-jakarta font-medium leading-6 text-[#2d2923] dark:text-[#fdfdfd] text-[16px] overflow-hidden text-ellipsis whitespace-nowrap">
          {item.project_name || 'Project'}
        </p>
      </div>
    </div>
  )
})

CategoryItemComponent.displayName = 'CategoryItemComponent'

export const CategoriesDesktop = ({
  items,
  categories = defaultCategories,
  selectedCategory = '',
  onCategoryChange,
  onItemClick,
  loading = false
}: CategoriesBaseProps) => {
  const categoriesRef = useRef<HTMLDivElement | null>(null)

  const filteredItems = useMemo(() => {
    return selectedCategory
      ? items.filter(item => item.category === selectedCategory || item.industry === selectedCategory)
      : items
  }, [items, selectedCategory])

  const handleCategoryClick = useCallback((category: string) => {
    onCategoryChange?.(category)
  }, [onCategoryChange])

  const categoriesWithOrders = useMemo(() => {
    const allCategories = categories.length > 0 ? categories : defaultCategories
    return allCategories.filter(category => {
      return items.some(item => item.category === category || item.industry === category)
    })
  }, [categories, items])

  const displayCategories = categoriesWithOrders
  const activeCategory = (selectedCategory && displayCategories.includes(selectedCategory))
    ? selectedCategory
    : displayCategories[0] || ''

  const scrollCategories = useCallback((direction: 'left' | 'right') => {
    const el = categoriesRef.current
    if (!el) return
    el.scrollBy({ left: direction === 'left' ? -180 : 180, behavior: 'smooth' })
  }, [])

  if (loading) {
    return (
      <div className="h-full flex flex-col px-[23px] pt-[23px] pb-[23px] relative">
        <p className="font-plus-jakarta font-medium leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] text-base uppercase">
          <span className="inline-flex items-center gap-2">
            Categories
            <InfoTooltip content="Browse Secondaries orders by industry/category." ariaLabel="Categories info" />
          </span>
        </p>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] text-sm">
            Loading categories...
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col px-[23px] pt-[23px] pb-[23px]">
      <p className="font-plus-jakarta font-medium leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] text-base uppercase">
        <span className="inline-flex items-center gap-2">
          Categories
          <InfoTooltip content="Browse Secondaries orders by industry/category." ariaLabel="Categories info" />
        </span>
      </p>

      <div className="mb-[15px] mt-[24px] relative group">
        <button
          onClick={() => scrollCategories('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-6 w-6 flex items-center justify-center bg-white/90 dark:bg-[#1c1c1c]/90 border border-[rgba(45,41,35,0.12)] dark:border-[rgba(253,253,253,0.12)] opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Scroll categories left"
        >
          <ChevronLeft className="w-4 h-4 text-[#2d2923] dark:text-[#fdfdfd]" />
        </button>
        <div ref={categoriesRef} className="overflow-x-auto scrollbar-hide">
          <div className="flex gap-5 items-center min-w-max">
          {displayCategories.map((category) => {
            const isSelected = category === activeCategory
            return (
              <button
                key={category}
                onClick={() => handleCategoryClick(category)}
                className={`font-plus-jakarta leading-5 text-sm pb-2 pt-1 whitespace-nowrap flex-shrink-0 transition-colors ${
                  isSelected
                    ? 'font-semibold text-[#2d2923] dark:text-[#fdfdfd] border-b border-[#2d2923] dark:border-[#fdfdfd]'
                    : 'font-medium text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] hover:text-[#2d2923] dark:hover:text-[#fdfdfd]'
                }`}
              >
                {category}
              </button>
            )
          })}
          </div>
        </div>
        <button
          onClick={() => scrollCategories('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-6 w-6 flex items-center justify-center bg-white/90 dark:bg-[#1c1c1c]/90 border border-[rgba(45,41,35,0.12)] dark:border-[rgba(253,253,253,0.12)] opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Scroll categories right"
        >
          <ChevronRight className="w-4 h-4 text-[#2d2923] dark:text-[#fdfdfd]" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto space-y-3">
        {filteredItems.map((item, index) => (
          <CategoryItemComponent
            key={item.order_id || index}
            item={item}
            index={index}
            onItemClick={onItemClick}
          />
        ))}

        {filteredItems.length === 0 && (
          <div className="text-center py-8 text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] text-sm">
            No items in this category
          </div>
        )}
      </div>
    </div>
  )
}
